import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import * as api from '../api.ts';
import { Directory, type DirectoryType } from '../directory.tsx';
import { Button, Empty, Finding, PageTitle, Spinner } from '../ui.tsx';
import { Sites } from './readiness.tsx';
import { WarningKindIcon } from '../warning-icons.tsx';
import { useShowMinorWarnings } from '../warning-filter.tsx';

/**
 * The site's full location as one line: "Suite 1, Level 8, 420 St Kilda Rd"
 * (migration 0110). Unit as typed, level prefixed with "Level ", then the
 * street address — each part omitted when the site has none, so a whole-building
 * site reads as just its address and a site with nothing returns null.
 */
export function locationLine(s: {
  unit: string | null;
  level: string | null;
  address: string | null;
}): string | null {
  const parts = [s.unit, s.level ? `Level ${s.level}` : null, s.address].filter((p): p is string =>
    Boolean(p && p.trim()),
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Locations — its own rail directory, on the shared rail-directory shell
 * (`directory.tsx`): **a type column of building groups, a list of the org's
 * buildings, and — when one is selected — that building as a FULL PAGE in the
 * content region.** Promoted out of Settings (owner IA, 2026-09-07) because a
 * building is a first-class entity an admin manages directly, not org-level
 * config.
 *
 * The two states of the screen:
 *
 * - **The list** (no building selected). The shared rail-directory shell: a
 *   **type column** of building groups on the left — "All buildings", and once
 *   an org holds a mix of ordinary and sensitive sites, "Standard" and
 *   "Restricted" so a courthouse or refuge can be found apart from the shops
 *   (the split is a property of the data, `sites.sensitive`, never a preference,
 *   and only appears when there is genuinely a mix) — and the buildings
 *   directory on the right. The directory is the shared `Sites` list view
 *   (readiness.tsx) driven by `view="list"`, which carries the search, the
 *   public links and the zero-state wizard, so that behaviour (PR #413) is
 *   reused rather than duplicated; the shared header owns the screen title
 *   (`hideTitle`) and the "Add building" CTA, and the type column filters which
 *   buildings the list is handed.
 * - **The building page** (a building selected). Selecting a building **replaces
 *   the list with the building, full width** — the app rail persists, the type
 *   column and directory give way to the whole building detail, and a clear
 *   **back to all buildings** control returns to the list. This deliberately
 *   supersedes `docs/design-dashboard.md` §11's "detail lives in the floating
 *   glass panel" rule **for buildings only** (product-owner decision, Trav
 *   2026-09-13): a building is heavy enough — summary, details, areas, assembly
 *   points, procedures, wardens/ECO, equipment, followers, diagrams, PEEP, and
 *   the alert audience — that a slide-over drawer was the wrong container.
 *   Lighter entities (a person, a team) keep the slide-over. The page is the
 *   `Sites` detail body (`view="detail"`): the summary header read at a glance
 *   (with a raise/run-a-drill action) over the ordered edit sections, and it is
 *   a real route (`/locations/<siteId>`, resolved in `main.tsx`) so back,
 *   forward and reload land on the building an admin was looking at.
 *
 * The **Add building** CTA now opens the full-screen building setup wizard at
 * its own route (`/locations/new`, piece 2), reached only from the list's header
 * CTA. It is no longer a slide-over drawer: setting up a whole building — place
 * it, assembly points, areas, wardens, people — is a page, the same call the
 * building *detail* made when it stopped being a drawer.
 *
 * The buildings are the org's **Locations** — never "Places", which is reserved
 * for the consumer app. The map is the persistent substrate under this screen
 * like every other; `Sites` and `Eco` (from readiness.tsx) are composed here
 * rather than duplicated, so their existing behaviour and tests stand.
 */
export function Locations({
  orgId,
  sites,
  sitesLoading = false,
  siteId,
  onSite,
  onReview,
  onSetup,
  onRaise,
  onPeople,
  onAddBuilding,
}: {
  orgId: string;
  sites: api.Site[];
  /**
   * Whether the sites query is still in flight. On a reload of
   * `/locations/<id>` the building comes from the URL before the sites list has
   * loaded, so without this the page would flash the directory (an empty
   * `sites`) before resolving the building. While loading, the page holds a
   * loading affordance instead of the list.
   */
  sitesLoading?: boolean;
  siteId: string;
  onSite: (id: string) => void;
  /** Where a building's gaps are actually listed — the readiness report. */
  onReview: () => void;
  /** Into the guided wizard, for an org that has no buildings yet. */
  onSetup?: () => void;
  /**
   * Open the full-screen building setup wizard (`/locations/new`) — the header
   * "Add building" CTA. Optional so a test/embedding that never adds a building
   * can omit it; when absent the CTA is not offered.
   */
  onAddBuilding?: () => void;
  /** Into the raise/drill flow for the selected building (the detail summary). */
  onRaise?: () => void;
  /**
   * Open the People screen filtered to a building — the building page's "Alert
   * audience" drill-down to the named list, reusing People-filtered-by-site
   * rather than duplicating a roster table.
   */
  onPeople?: (siteId: string) => void;
}) {
  const selected = sites.find((s) => s.id === siteId);

  // The "Add building" CTA opens the full-screen building setup wizard at its
  // own route (piece 2), never a drawer or an inline form under the list.
  // Offered only once the org has a building — the zero state is the guided
  // wizard, reached through `onSetup`, not the loose add.

  // The buildings search lives in the shared Directory header now (item 2), so
  // the list renders edge-to-edge under it rather than boxed with an in-body
  // search. Owned here so the header can carry the input and the count meta.
  const [search, setSearch] = useState('');

  // The type column: building groups. "Standard" / "Restricted" appear only
  // when the org actually holds a mix, so a group is never empty (an empty group
  // would drop the list into its zero-state wizard, which is the wrong sentence
  // for "you filtered").
  const [group, setGroup] = useState('all');
  const sensitiveCount = sites.filter((s) => s.sensitive).length;
  const hasMix = sensitiveCount > 0 && sensitiveCount < sites.length;
  const types: DirectoryType[] = [
    { id: 'all', label: 'All buildings', count: sites.length },
    ...(hasMix
      ? [
          { id: 'standard', label: 'Standard', count: sites.length - sensitiveCount },
          { id: 'restricted', label: 'Restricted', count: sensitiveCount },
        ]
      : []),
  ];
  const shownSites =
    group === 'standard'
      ? sites.filter((s) => !s.sensitive)
      : group === 'restricted'
        ? sites.filter((s) => s.sensitive)
        : sites;

  // Meta mirrors People: "N buildings", plus "N of M shown" when the header
  // search narrows the group-filtered list. The predicate matches the one Sites
  // filters by (name + address), so the count never disagrees with the list.
  const q = search.trim().toLowerCase();
  const shownCount = q
    ? shownSites.filter((s) => `${s.name} ${locationLine(s) ?? ''}`.toLowerCase().includes(q))
        .length
    : shownSites.length;
  const meta =
    sites.length > 0 ? (
      <>
        {shownSites.length} {shownSites.length === 1 ? 'building' : 'buildings'}
        {q && shownCount !== shownSites.length ? (
          <span className="text-[var(--ink-3)]">
            {' · '}
            {shownCount} of {shownSites.length} shown
          </span>
        ) : null}
      </>
    ) : undefined;

  // A building is named in the URL: this is the building page, not the list.
  // Three cases, and each has to be honest about what it knows:
  //
  // - the building resolved: show it as a full page, replacing the list;
  // - the sites list is still loading: hold a loading affordance rather than
  //   flashing the directory first (a reload of `/locations/<id>` arrives here
  //   with `sites` still empty);
  // - the list has loaded and the id matches nothing: a short not-found state,
  //   never a silent fall-through to the directory, which would read as "you are
  //   browsing buildings" when the truth is "the one you opened is gone".
  const backControl = (
    <div className="mb-4">
      <Button variant="quiet" label="Back to all buildings" onClick={() => onSite('')}>
        <span aria-hidden>&lsaquo;</span> All buildings
      </Button>
    </div>
  );

  if (siteId) {
    if (selected) {
      return (
        <div>
          {/* The building page's own header: a back control to the buildings
              list, then the building's name and address as the page title. The
              detail body below carries its own summary header, sections and the
              ECO board unchanged — only the container changed from drawer to
              page. */}
          {backControl}
          <PageTitle sub={locationLine(selected) ?? undefined}>{selected.name}</PageTitle>

          {/* An agency's warning covering THIS building — attributed (W1),
              informational only (W3: a warning never enters the activation
              path; deciding what to do about weather stays with the org). */}
          <SiteWarnings orgId={orgId} siteId={siteId} />

          {/* The whole building detail — summary header, then the ordered edit
              sections (details, alert audience, areas, assembly, procedures, the
              Wardens/ECO board, equipment, followers, diagrams, PEEP). Reused
              exactly as it was in the drawer; only its container is a page now. */}
          <Sites
            orgId={orgId}
            sites={sites}
            siteId={siteId}
            onSite={onSite}
            onReview={onReview}
            onSetup={onSetup}
            onRaise={onRaise}
            onPeople={onPeople}
            view="detail"
          />
        </div>
      );
    }

    if (sitesLoading) {
      return (
        <div>
          {backControl}
          <Spinner>Loading this building</Spinner>
        </div>
      );
    }

    return (
      <div>
        {/* The back control above IS the "Back to all buildings" way out; the
            Empty carries only the sentence, so there is one control, not two. */}
        {backControl}
        <Empty>That building was not found — it may have been removed.</Empty>
      </div>
    );
  }

  return (
    <Directory
      title="Locations"
      meta={meta}
      typeColumnLabel="Building groups"
      types={types}
      activeType={group}
      onSelectType={setGroup}
      // Search moves up into the sticky header (item 2), offered only once the
      // org has a building to narrow.
      search={
        sites.length > 0
          ? {
              value: search,
              onChange: setSearch,
              label: 'Search buildings',
              placeholder: 'Name or address',
            }
          : undefined
      }
      actions={
        sites.length > 0 && onAddBuilding ? (
          <Button variant="primary" onClick={onAddBuilding}>
            Add building
          </Button>
        ) : undefined
      }
    >
      {/* The buildings list — the shared Sites list view, handed the
            type-column's slice, rendered edge-to-edge (bareList): no inner
            "Buildings" section title and no boxed scroll, the search lifted to
            the header. The add form moves to the overlay (hideAddForm), and the
            shared header owns the title (hideTitle). Selecting a building sets
            siteId, which swaps this whole screen for the building page above. */}
      <Sites
        orgId={orgId}
        sites={shownSites}
        siteId={siteId}
        onSite={onSite}
        onReview={onReview}
        onSetup={onSetup}
        view="list"
        hideTitle
        hideAddForm
        bareList
        search={search}
      />
    </Directory>
  );
}

/**
 * The in-force agency warnings whose area covers this building, as attention
 * lines above the detail. Reads the same org-wide join Home's card does (one
 * cached query serves both) and filters to this building; nothing renders
 * when the sky is clear, which is the common case.
 */
function SiteWarnings({ orgId, siteId }: { orgId: string; siteId: string }) {
  const showMinor = useShowMinorWarnings();
  const matches = useQuery({
    queryKey: ['warning-matches', orgId, showMinor],
    queryFn: () => api.getWarningMatches(orgId, showMinor),
    refetchInterval: 60_000,
  });
  const mine = (matches.data?.matches ?? []).filter((m) => m.site_id === siteId);
  if (mine.length === 0) return null;
  return (
    <div className="mb-4 space-y-2">
      {mine.map((w) => (
        <Finding key={w.id} tone="attention">
          <WarningKindIcon
            kind={w.event_kind}
            className="mr-1.5 inline-block align-[-2px] text-[var(--attention)]"
          />
          <strong>{w.source}:</strong> {w.headline}
          {w.expires_at ? (
            <span className="text-[var(--ink-2)]">
              {' '}
              — until {new Date(w.expires_at).toLocaleString()}
            </span>
          ) : null}
        </Finding>
      ))}
    </div>
  );
}
